import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
} from '@mui/material';
import {
  AttachMoney,
  Person,
  CheckCircle,
  Assignment,
  Star,
} from '@mui/icons-material';
import { driverEarningsService } from '@/services/driverEarningsService';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const DriverEarningsOverview: React.FC = () => {
  const [summary, setSummary] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await driverEarningsService.getEarningsSummary();
      console.log('Driver earnings summary:', data);
      setSummary(data);
    } catch (err: any) {
      console.error('Error fetching driver earnings summary:', err);
      const errorMessage = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || 'Failed to load earnings summary';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert severity="info">
        No earnings data available.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.lighter', borderLeft: 4, borderColor: 'success.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Earnings
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {formatCurrency(summary.totalEarnings)}
                  </Typography>
                  <Box sx={{ height: 20 }} /> {/* Spacer for alignment */}
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'success.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Drivers
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {summary.totalDrivers}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {summary.activeDrivers} active
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, color: 'info.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.lighter', borderLeft: 4, borderColor: 'warning.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pending Payouts
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {formatCurrency(summary.pendingPayouts)}
                  </Typography>
                  <Box sx={{ height: 20 }} /> {/* Spacer for alignment */}
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'warning.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.lighter', borderLeft: 4, borderColor: 'primary.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed Payouts
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {formatCurrency(summary.completedPayouts)}
                  </Typography>
                  <Box sx={{ height: 20 }} /> {/* Spacer for alignment */}
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'primary.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Average Earnings */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Average Earnings Per Driver
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {formatCurrency(summary.averageEarningsPerDriver)}
          </Typography>
        </CardContent>
      </Card>

      {/* Top Earning Drivers */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Top Earning Drivers
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>#</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Total Earnings</TableCell>
                  <TableCell align="right">Available Balance</TableCell>
                  <TableCell align="center">Trips</TableCell>
                  <TableCell align="center">Rating</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.topEarningDrivers && summary.topEarningDrivers.length > 0 ? (
                  summary.topEarningDrivers.map((driver: any, index: number) => (
                    <TableRow key={driver.driverId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {driver.driverName?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {driver.driverName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              #{driver.driverNumber}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{driver.driverEmail}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {formatCurrency(driver.totalEarnings)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                          {formatCurrency(driver.availableBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{driver.completedTrips}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <Star fontSize="small" sx={{ color: 'warning.main' }} />
                          <Typography variant="body2">{driver.rating.toFixed(1)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={driver.status}
                          size="small"
                          color={
                            driver.status === 'active'
                              ? 'success'
                              : 'default'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No driver earnings data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};
